/**
 * Shared automation progress banner.
 *
 * Renders one in-flight (or finished) `automation_jobs` row with:
 *  - status badge (running / queued / completed / failed)
 *  - progress bar w/ scenes_done / total + elapsed + ETA
 *  - per-scene pip grid (image + video status pips)
 *  - live activity log (last 12 step events, newest-first)
 *
 * Used on `/storyboards` (next to the channel idea generator) and
 * on `/review` (so the user lands here right after clicking
 * Automate and watches the build happen in real-time).
 *
 * Parent owns the polling + dismiss state — this component is
 * pure-presentational.
 */
import { Link } from "react-router-dom";
import { Image as ImageIcon, Film, Loader2, Check, X, FileText, ImageDown } from "lucide-react";

// Single-icon LED — replaces the two anonymous colored dots with
// recognizable image-vs-video glyphs and a state badge (✓/✗/⟳).
// The whole pip becomes a self-explanatory legend so the user can
// scan a 7-scene grid and know exactly which renders need
// attention without hovering for tooltips.
//
// `kind` accepts "image" | "video" | "thumbnail" | "description"
// so the same component renders both per-scene pips AND the
// job-level thumbnail/description chips.
function PipIcon({ kind, status, label }) {
  // `status`: "ok" | "failed" | "running" | "pending"
  const Glyph =
    kind === "image" ? ImageIcon
    : kind === "video" ? Film
    : kind === "thumbnail" ? ImageDown
    : kind === "description" ? FileText
    : ImageIcon;
  const color =
    status === "ok" ? "text-emerald-300"
    : status === "failed" ? "text-red-300"
    : status === "running" ? "text-yellow-300"
    : "text-zinc-600";
  const Badge =
    status === "ok" ? Check
    : status === "failed" ? X
    : status === "running" ? Loader2
    : null;
  const badgeColor =
    status === "ok" ? "text-emerald-300"
    : status === "failed" ? "text-red-300"
    : "text-yellow-300";
  return (
    <div className="relative flex items-center" title={`${label}: ${status}`}>
      <Glyph className={`w-3 h-3 ${color}`} />
      {Badge && (
        <Badge
          className={[
            "w-2.5 h-2.5 -ml-0.5 -mb-0.5 self-end",
            badgeColor,
            status === "running" ? "animate-spin" : "",
          ].join(" ")}
          strokeWidth={3}
        />
      )}
    </div>
  );
}

export default function AutomationProgressBanner({ job, onDismiss, onCancel, onOpenStoryboard, onResume }) {
  const running = job.status === "running";
  const completed = job.status === "completed";
  const failed = job.status === "failed";
  const queued = job.status === "queued";
  const cancelled = job.status === "cancelled";
  const total = job.total_scenes || 0;
  const done = job.scenes_done || 0;
  const pct = total ? Math.round(100 * Math.min(1, done / total)) : 0;

  // STALL DETECTION: if the job claims to be running but the activity
  // log hasn't ticked in 3+ minutes, the background worker has almost
  // certainly been killed by a pod restart (eg user deployed a fix
  // mid-render). Surface a "Resume Now" button so the user can re-launch
  // the worker without waiting for another deploy. The backend
  // /storyboards/automate/resume-orphans endpoint is safe to call
  // repeatedly — `_run_per_scene_renders`'s resume-check skips scenes
  // that already rendered cleanly.
  const lastStep = (job.steps && job.steps.length)
    ? job.steps[job.steps.length - 1]
    : null;
  const lastStepTs = lastStep && lastStep.ts
    ? new Date(lastStep.ts).getTime()
    : (job.started_at ? new Date(job.started_at).getTime() : 0);
  const sinceLastMs = lastStepTs ? Date.now() - lastStepTs : 0;
  const STALL_THRESHOLD_MS = 3 * 60 * 1000;
  const looksStalled = running && sinceLastMs > STALL_THRESHOLD_MS;

  // Derive per-scene outcomes from the steps log so we can show
  // an honest success/failure split. The backend bumps
  // `scenes_done` for both passes (succeeded AND failed) which
  // means status='completed' is really "all scenes processed",
  // NOT "all scenes succeeded". Without this derivation the
  // banner showed "100% COMPLETE" in green even when 5 of 7
  // videos timed out — which let the user think the job worked
  // when in reality most scenes still need re-renders.
  const stepsForOutcome = job.steps || [];
  const sceneOutcomes = {};  // {idx: {image: ok|failed, video: ok|failed}}
  for (const s of stepsForOutcome) {
    const idx = s.scene_idx;
    if (idx == null || idx < 0) continue;
    if (s.step !== "image" && s.step !== "video") continue;
    if (!sceneOutcomes[idx]) sceneOutcomes[idx] = {};
    if (s.status === "ok" || s.status === "failed") {
      sceneOutcomes[idx][s.step] = s.status;
    }
  }
  let scenesSucceeded = 0;
  let scenesFailed = 0;
  for (const idx in sceneOutcomes) {
    const o = sceneOutcomes[idx];
    if (o.image === "ok" && o.video === "ok") scenesSucceeded += 1;
    else if (o.image === "failed" || o.video === "failed") scenesFailed += 1;
  }
  // A "completed" job that finished with some failures is NOT a
  // green-bar success — paint it amber and tell the user to hit
  // Finish Automation to retry.
  const completedWithErrors = completed && scenesFailed > 0;
  const successPct = total ? Math.round(100 * scenesSucceeded / total) : 0;

  // Derive JOB-LEVEL artifact states: thumbnail and SEO description.
  // These aren't per-scene — they happen once per storyboard during
  // the automation pipeline. Rendering them as their own icons next
  // to the elapsed/eta line lets the user see at a glance whether
  // the storyboard "ships" (has thumb + description) without
  // scrolling through scene pips.
  //
  // Thumbnail: kicked off at storyboard creation BEFORE per-scene
  //   work; presence on `job.thumbnail_url` (set by automation_service
  //   from create_res) signals success. If creation failed, the
  //   storyboard wouldn't exist and the job would never have
  //   gotten this far — so a missing thumbnail_url here means
  //   "still creating" (running) or "creation failed" (terminal).
  // Description: emitted as a `step: "description"` event with
  //   scene_idx=-1 in the activity log when the SEO pass runs.
  const thumbStatus = (() => {
    if (job.thumbnail_url) return "ok";
    if (job.thumbnail_failed) return "failed";
    if (running || queued) return "running";
    return "pending";
  })();
  const descStep = stepsForOutcome.filter(
    (s) => s.step === "description"
  ).slice(-1)[0];
  const descStatus = (() => {
    if (!descStep) return running ? "pending" : "pending";
    if (descStep.status === "ok") return "ok";
    if (descStep.status === "failed") return "failed";
    if (descStep.status === "started") return "running";
    return "pending";
  })();
  // "Assume all previews are approved → stitch" — emitted as a
  // `step: "assemble"` event with scene_idx=-1 at the tail of the
  // automation. `ok` here means the stitch has been kicked off
  // (the actual ffmpeg work continues in background and surfaces
  // via the storyboard's `assembled_status` field).
  const assembleStep = stepsForOutcome.filter(
    (s) => s.step === "assemble"
  ).slice(-1)[0];
  const assembleStatus = (() => {
    if (!assembleStep) return running ? "pending" : "pending";
    if (assembleStep.status === "ok") return "ok";
    if (assembleStep.status === "failed") return "failed";
    if (assembleStep.status === "started") return "running";
    return "pending";
  })();

  // Build a per-scene status map by walking `steps[]`. Each scene
  // has two slots: image + video. Most-recent status wins (so a
  // "failed" → "running" re-render shows running).
  const steps = job.steps || [];
  const sceneStatus = {};
  for (const s of steps) {
    const idx = s.scene_idx;
    if (idx == null || idx < 0) continue;  // -1 = description/meta step
    if (!sceneStatus[idx]) sceneStatus[idx] = { image: null, video: null, detail: "" };
    if (s.step === "image" || s.step === "video") {
      sceneStatus[idx][s.step] = s.status;
      if (s.status === "failed" && s.detail) {
        sceneStatus[idx].detail = `${s.step}: ${s.detail}`;
      }
    }
  }
  // GROUND-TRUTH OVERRIDE: the steps[] history is a *log* — if an
  // older run logged `video failed` and the current resume-skip
  // never wrote a fresh `video ok` (eg because of the recently-fixed
  // `video_versions` field-name bug), the pip would lie. The
  // backend now sends `scene_state[]` with the ACTUAL image/video
  // presence per scene. If the asset is on disk we force the pip to
  // green regardless of what the steps log says — what the user
  // sees on the storyboard is what they should see in the banner.
  const sceneState = Array.isArray(job.scene_state) ? job.scene_state : [];
  for (let i = 0; i < sceneState.length; i++) {
    const realState = sceneState[i] || {};
    if (!sceneStatus[i]) sceneStatus[i] = { image: null, video: null, detail: "" };
    if (realState.image_present && sceneStatus[i].image !== "running") {
      sceneStatus[i].image = "ok";
    }
    if (realState.video_present && sceneStatus[i].video !== "running") {
      sceneStatus[i].video = "ok";
      // Clear stale failure messages now that the video is actually
      // present — keeping the message would mislead.
      if (sceneStatus[i].detail && sceneStatus[i].detail.startsWith("video:")) {
        sceneStatus[i].detail = "";
      }
    }
  }
  const activeIdx = running ? job.current_scene_idx : null;
  const activeStep = job.current_step;

  // Elapsed + ETA.
  const startedAt = job.started_at ? new Date(job.started_at).getTime() : null;
  const completedAt = job.completed_at ? new Date(job.completed_at).getTime() : null;
  const now = Date.now();
  const elapsedMs = startedAt ? (completedAt || now) - startedAt : 0;
  const fmtDur = (ms) => {
    if (ms <= 0) return "0s";
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}m ${r.toString().padStart(2, "0")}s`;
  };
  let etaText = "";
  if (running && done > 0 && total > done && startedAt) {
    const avgPerScene = elapsedMs / done;
    const eta = avgPerScene * (total - done);
    etaText = `eta ${fmtDur(eta)}`;
  }

  // Open-storyboard handler defaults to a Link if the parent didn't
  // pass one, so the banner is always actionable.
  const openButton = onOpenStoryboard ? (
    <button
      type="button"
      data-testid={`channel-automation-open-${job.job_id}`}
      onClick={onOpenStoryboard}
      className="font-mono text-[10px] uppercase tracking-[0.25em] border-2 border-yellow-400 text-yellow-100 hover:text-black hover:bg-yellow-300 px-3 py-1.5 rounded-sm transition-colors"
    >
      Open storyboard →
    </button>
  ) : job.storyboard_id ? (
    <Link
      to={`/storyboards/${job.storyboard_id}`}
      data-testid={`channel-automation-open-${job.job_id}`}
      className="font-mono text-[10px] uppercase tracking-[0.25em] border-2 border-yellow-400 text-yellow-100 hover:text-black hover:bg-yellow-300 px-3 py-1.5 rounded-sm transition-colors"
    >
      Open storyboard →
    </Link>
  ) : null;

  return (
    <div
      data-testid="channel-automation-banner"
      className={[
        "border-2 rounded-sm p-4 space-y-3",
        completedWithErrors ? "border-amber-400 bg-amber-400/10"
        : completed ? "border-emerald-400 bg-emerald-400/10"
        : failed ? "border-red-400 bg-red-400/10"
        : cancelled ? "border-zinc-500 bg-zinc-800/40"
        : queued ? "border-zinc-600 bg-zinc-900/60"
        : "border-cyan-400 bg-cyan-400/10",
      ].join(" ")}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="space-y-0.5">
          <div className={[
            "font-mono text-[10px] uppercase tracking-[0.3em]",
            completedWithErrors ? "text-amber-200"
            : completed ? "text-emerald-200"
            : failed ? "text-red-200"
            : cancelled ? "text-zinc-400"
            : "text-cyan-200",
          ].join(" ")}>
            {completedWithErrors
              ? `// COMPLETE WITH ERRORS · ${scenesSucceeded} of ${total} scenes succeeded · ${scenesFailed} failed`
            : completed ? "// COMPLETE"
            : failed ? "// FAILED"
            : cancelled ? "// CANCELLED"
            : queued ? "// QUEUED · WAITING FOR PRIOR JOB"
            : "// IN PROGRESS"}
          </div>
          <div className="text-base font-bold text-white">
            {job.video_name || "Untitled"}
          </div>
          {completedWithErrors && (
            <div className="text-[11px] text-amber-100 font-mono pt-1">
              ↻ click <span className="text-amber-300 font-bold">Finish Automation</span> on this story below to retry the failed scenes
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {openButton}
          {looksStalled && onResume && (
            <button
              type="button"
              data-testid={`channel-automation-resume-${job.job_id}`}
              onClick={onResume}
              title={`No activity for ${Math.floor(sinceLastMs / 60000)}m — the backend may have restarted. Click to re-launch the worker.`}
              className="font-mono text-[10px] uppercase tracking-[0.25em] border-2 border-amber-300/70 text-amber-100 hover:text-black hover:bg-amber-300 px-3 py-1.5 rounded-sm transition-colors animate-pulse"
            >
              ↻ Resume Now
            </button>
          )}
          {(running || queued) && onCancel && (
            <button
              type="button"
              data-testid={`channel-automation-cancel-${job.job_id}`}
              onClick={onCancel}
              className="font-mono text-[10px] uppercase tracking-[0.25em] border-2 border-red-400/60 text-red-200 hover:text-black hover:bg-red-300 px-3 py-1.5 rounded-sm transition-colors"
            >
              Cancel
            </button>
          )}
          {!running && !queued && onDismiss && (
            <button
              type="button"
              data-testid={`channel-automation-dismiss-${job.job_id}`}
              onClick={onDismiss}
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-zinc-100 px-2 py-1.5"
            >
              dismiss
            </button>
          )}
        </div>
      </div>

      {queued && (
        <div className="text-[11px] text-zinc-300 font-mono">
          {total} scenes ready · will start automatically when the previous job finishes
        </div>
      )}
      {!queued && (
      <>
      <div>
        <div className="flex items-baseline justify-between text-[11px] font-mono mb-1">
          <span className="text-zinc-200">
            {completedWithErrors ? (
              <>
                <span className="text-amber-100 font-bold">{scenesSucceeded}/{total}</span> scenes succeeded · {successPct}% · <span className="text-red-300">{scenesFailed} failed</span>
              </>
            ) : (
              <>
                <span className="text-white font-bold">{done}/{total}</span> scenes built · {pct}%
              </>
            )}
          </span>
          <span className="text-zinc-400 flex items-center gap-2">
            <span
              data-testid={`channel-automation-duration-${job.job_id}`}
              className={completed && !running ? "text-emerald-300" : ""}
            >
              {completed && !running ? "built in " : "elapsed "}{fmtDur(elapsedMs)}{etaText ? ` · ${etaText}` : ""}
            </span>
            <span className="inline-flex items-center gap-1 ml-1 pl-2 border-l border-zinc-700">
              <PipIcon
                kind="thumbnail"
                label="YouTube thumbnail"
                status={thumbStatus}
              />
              <PipIcon
                kind="description"
                label="YouTube description"
                status={descStatus}
              />
              <PipIcon
                kind="video"
                label="Final stitched video"
                status={assembleStatus}
              />
            </span>
          </span>
        </div>
        <div className="h-2 bg-black/40 rounded-sm overflow-hidden">
          <div
            className={[
              "h-full transition-all duration-500",
              completedWithErrors ? "bg-amber-400"
              : completed ? "bg-emerald-400"
              : failed ? "bg-red-400"
              : cancelled ? "bg-zinc-500"
              : "bg-cyan-400 animate-pulse",
            ].join(" ")}
            style={{
              width: completedWithErrors
                ? `${successPct}%`
                : `${completed ? 100 : pct}%`,
            }}
            data-testid="channel-automation-progress-bar"
          />
        </div>
      </div>

      {total > 0 && (
        <div
          data-testid="channel-automation-scene-grid"
          className="grid gap-1.5"
          style={{ gridTemplateColumns: `repeat(${Math.min(total, 12)}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: total }).map((_, i) => {
            const st = sceneStatus[i] || {};
            const isActive = activeIdx === i && running;
            const imgOk = st.image === "ok";
            const imgFail = st.image === "failed";
            const vidOk = st.video === "ok";
            const vidFail = st.video === "failed";
            const fullyDone = imgOk && vidOk;
            return (
              <div
                key={`scene-pip-${i}`}
                title={
                  fullyDone ? `Scene ${i + 1}: done`
                  : isActive ? `Scene ${i + 1}: ${activeStep === "image" ? "rendering image…" : activeStep === "video" ? "rendering video…" : "starting…"}`
                  : st.detail ? `Scene ${i + 1}: ${st.detail}`
                  : `Scene ${i + 1}: pending`
                }
                className={[
                  "border-2 rounded-sm p-1.5 transition-colors",
                  isActive ? "border-yellow-300 bg-yellow-300/10 animate-pulse"
                  : fullyDone ? "border-emerald-400 bg-emerald-400/10"
                  : (imgFail || vidFail) ? "border-red-400 bg-red-400/10"
                  : "border-zinc-700 bg-black/40",
                ].join(" ")}
              >
                <div className="text-[9px] font-mono text-zinc-400 leading-tight">
                  S{i + 1}
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <PipIcon
                    kind="image"
                    label="Scene image"
                    status={
                      imgOk ? "ok"
                      : imgFail ? "failed"
                      : isActive && activeStep === "image" ? "running"
                      : "pending"
                    }
                  />
                  <PipIcon
                    kind="video"
                    label="Scene video"
                    status={
                      vidOk ? "ok"
                      : vidFail ? "failed"
                      : isActive && activeStep === "video" ? "running"
                      : "pending"
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {steps.length > 0 && (
        <details
          data-testid="channel-automation-log"
          className="border-t border-cyan-400/20 pt-2"
          open
        >
          <summary className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-200 cursor-pointer select-none">
            // ACTIVITY LOG · {steps.length} {steps.length === 1 ? "event" : "events"}
          </summary>
          <ul className="mt-1.5 space-y-0.5 max-h-40 overflow-auto pr-1">
            {steps.slice(-12).reverse().map((s, i) => {
              const time = s.ts ? new Date(s.ts).toLocaleTimeString([], {
                hour: "2-digit", minute: "2-digit", second: "2-digit",
              }) : "";
              const isMeta = s.step === "description" || (s.scene_idx ?? 0) < 0;
              const sc = (s.scene_idx ?? 0) + 1;
              return (
                <li
                  key={`step-${steps.length - i}`}
                  className="font-mono text-[11px] leading-snug flex items-baseline gap-2"
                >
                  <span className="text-zinc-500 tabular-nums">{time}</span>
                  <span className="text-cyan-200">
                    {isMeta ? "finalize" : `scene ${sc}`}
                  </span>
                  <span className="text-zinc-300">{s.step}</span>
                  <span
                    className={
                      s.status === "ok" ? "text-emerald-300 font-bold"
                      : s.status === "failed" ? "text-red-300 font-bold"
                      : "text-zinc-300"
                    }
                  >
                    {s.status}
                  </span>
                  {s.detail && (
                    <span className="text-zinc-400 truncate">· {s.detail}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      )}

      {failed && job.halt_kind === "insufficient_credits" && (
        <div
          data-testid="halt-credits-banner"
          className="border-2 border-red-400 bg-red-500/10 rounded-sm p-3 space-y-1"
        >
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-red-200">
            // OUT OF SEEDANCE CREDITS
          </div>
          <div className="text-sm text-red-100">
            All your queued jobs were stopped because Atlas Cloud reported insufficient credits.
            {" "}
            <a
              href="https://atlascloud.ai/billing"
              target="_blank"
              rel="noreferrer"
              className="underline text-red-200 hover:text-white"
            >
              Top up your balance →
            </a>
            {" "}
            then click <span className="font-bold text-amber-200">Finish Automation</span> on each stopped story to resume.
          </div>
        </div>
      )}

      {failed && job.halt_kind === "auth_invalid" && (
        <div
          data-testid="halt-auth-banner"
          className="border-2 border-red-400 bg-red-500/10 rounded-sm p-3 space-y-1"
        >
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-red-200">
            // SEEDANCE API KEY INVALID
          </div>
          <div className="text-sm text-red-100">
            All your queued jobs were stopped because Atlas Cloud rejected your API key. Fix <span className="font-mono">SEEDANCE_API_KEY</span> and contact Emergent Support if this looks like a deployment config issue.
          </div>
        </div>
      )}

      {failed && job.error && !job.halt_kind && (
        <div className="text-[11px] text-red-200 font-mono">
          {job.error}
        </div>
      )}
      </>
      )}
    </div>
  );
}
